import React, { useState, useEffect } from 'react';
import { Event } from '../../../types/event';
import { useImageUpload } from '../../../hooks/useImageUpload';
import { useContests } from '../../../hooks/useContests';
import { useToast } from '../../../hooks/useToast';
import { Calendar, Clock, Image as ImageIcon, Loader2 } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface EventFormProps {
  initialData?: Partial<Event>;
  onSubmit: (data: Partial<Event>) => Promise<void>;
  isSubmitting: boolean;
}

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    [{ 'header': 1 }, { 'header': 2 }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'script': 'sub'}, { 'script': 'super' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'direction': 'rtl' }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'font': [] }],
    [{ 'align': [] }],
    ['clean'],
    ['link', 'image']
  ],
};

const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image', 'video'
];

const EventForm: React.FC<EventFormProps> = ({
  initialData = {},
  onSubmit,
  isSubmitting
}) => {
  const [title, setTitle] = useState(initialData.title || '');
  const [slug, setSlug] = useState(initialData.slug || '');
  const [description, setDescription] = useState(initialData.description || '');
  const [location, setLocation] = useState(initialData.location || '');
  // Définir les dates par défaut (maintenant pour le début, +1 jour pour la fin)
  const now = new Date();
  const defaultStartDate = now.toISOString().slice(0, 16);
  const defaultEndDate = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16);

  const [startDate, setStartDate] = useState(
    initialData.start_date 
      ? new Date(initialData.start_date.toString()).toISOString().slice(0, 16) 
      : defaultStartDate
  );
  const [endDate, setEndDate] = useState(
    initialData.end_date 
      ? new Date(initialData.end_date.toString()).toISOString().slice(0, 16) 
      : defaultEndDate
  );
  const [isVirtual, setIsVirtual] = useState(initialData.is_virtual || false);
  const [capacity, setCapacity] = useState(initialData.capacity?.toString() || '');
  const [contestId, setContestId] = useState(initialData.contest_id || '');
  
  // États de validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  
  const [imageUrl, setImageUrl] = useState(initialData.image_url || '');
  const [isDragging, setIsDragging] = useState(false);
  const { uploadImage, isUploading } = useImageUpload();
  const { contests, isLoading: loadingContests } = useContests();
  const { showToast } = useToast();

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

  const handleImageDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file && ACCEPTED_IMAGE_TYPES.includes(file.type) && file.size <= MAX_FILE_SIZE) {
      handleImageFile(file);
    } else {
      showToast('Format de fichier non supporté ou taille trop grande (max 5MB)', 'error');
    }
  };

  const handleImageFile = async (file: File) => {
    try {
      const uploadedUrl = await uploadImage(file);
      if (uploadedUrl) {
        setImageUrl(uploadedUrl);
      }
    } catch (error) {
      showToast('Erreur lors du téléchargement de l\'image', 'error');
    }
  };

  const handleImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (ACCEPTED_IMAGE_TYPES.includes(file.type) && file.size <= MAX_FILE_SIZE) {
        handleImageFile(file);
      } else {
        showToast('Format de fichier non supporté ou taille trop grande (max 5MB)', 'error');
      }
    }
  };
  
  useEffect(() => {
    if (initialData.image_url) {
      setImageUrl(initialData.image_url);
    }
  }, [initialData.image_url]);
  

  
  // Fonction de validation des champs
  const validateField = (name: string, value: string) => {
    let fieldErrors: Record<string, string> = {};
    
    switch (name) {
      case 'title':
        if (!value.trim()) {
          fieldErrors.title = 'Le titre est obligatoire';
        }
        break;
      case 'startDate':
        if (!value) {
          fieldErrors.startDate = 'La date de début est obligatoire';
        }
        break;
      case 'endDate':
        if (value && startDate && new Date(value) <= new Date(startDate)) {
          fieldErrors.endDate = 'La date de fin doit être postérieure à la date de début';
        }
        break;
      case 'capacity':
        if (value && (isNaN(Number(value)) || Number(value) <= 0)) {
          fieldErrors.capacity = 'La capacité doit être un nombre positif';
        }
        break;
    }
    
    return fieldErrors;
  };
  
  // Gestion du changement de sélection de concours
  const handleContestChange = (value: string) => {
    setContestId(value);
    
    // Si un concours est sélectionné et qu'aucune image n'est encore définie
    if (value && !imageUrl) {
      const selectedContest = contests?.find(contest => contest.id === value);
      if (selectedContest?.logo_url) {
        setImageUrl(selectedContest.logo_url);
      }
    }
  };
  
  // Gestion du changement des champs avec validation
  const handleFieldChange = (name: string, value: any) => {
    // Mettre à jour l'état du champ
    switch (name) {
      case 'title':
        setTitle(value);
        setSlug(generateSlug(value));
        break;
      case 'description': setDescription(value); break;
      case 'location': setLocation(value); break;
      case 'startDate': setStartDate(value); break;
      case 'endDate': setEndDate(value); break;
      case 'isVirtual': setIsVirtual(value); break;
      case 'capacity': setCapacity(value); break;
      case 'contestId': handleContestChange(value); break;
    }
    
    // Marquer le champ comme touché
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Valider le champ
    const fieldErrors = validateField(name, value);
    setErrors(prev => ({ ...prev, ...fieldErrors }));
  };
  
  // Validation du formulaire complet
  const validateForm = () => {
    const formErrors: Record<string, string> = {};
    
    // Valider chaque champ
    Object.assign(formErrors, validateField('title', title));
    Object.assign(formErrors, validateField('startDate', startDate));
    if (endDate) Object.assign(formErrors, validateField('endDate', endDate));
    if (capacity) Object.assign(formErrors, validateField('capacity', capacity));
    
    setErrors(formErrors);
    setTouched({
      title: true,
      startDate: true,
      endDate: !!endDate,
      capacity: !!capacity,
      location: !!location,
      description: !!description,
      contestId: !!contestId
    });
    
    return Object.keys(formErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Valider tous les champs avant soumission
    if (!validateForm()) {
      showToast('Veuillez corriger les erreurs dans le formulaire', 'error');
      return;
    }
    
    const eventData: Partial<Event> = {
      title,
      slug,
      description: description || undefined,
      location: location || undefined,
      start_date: new Date(startDate),
      end_date: endDate ? new Date(endDate) : undefined,
      is_virtual: isVirtual,
      capacity: capacity ? parseInt(capacity) : undefined,
      contest_id: contestId || undefined,
      image_url: imageUrl || undefined
    };
    
    try {
      await onSubmit(eventData);
    } catch (error) {
      // Error is handled in the parent component
    }
  };

  // Génération du slug à partir du titre (similaire à CampaignForm)
  function generateSlug(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '')
      .replace(/-{2,}/g, '-')
      .substring(0, 80);
  }
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-200 mb-1">
          Titre <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={title}
          onChange={(e) => handleFieldChange('title', e.target.value)}
          onBlur={() => setTouched(prev => ({ ...prev, title: true }))}
          aria-invalid={errors.title ? 'true' : 'false'}
          aria-describedby={errors.title ? 'title-error' : undefined}
          required
          className={`w-full px-3 py-2 bg-white/5 border ${errors.title && touched.title ? 'border-red-500' : 'border-white/10'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100 placeholder-gray-400`}
        />
        {errors.title && touched.title && (
          <p id="title-error" className="mt-1 text-sm text-red-400">{errors.title}</p>
        )}
      </div>
      <div>
        <label htmlFor="slug" className="block text-sm font-medium text-gray-200 mb-1">
          Slug
        </label>
        <input
          type="text"
          id="slug"
          name="slug"
          value={slug}
          readOnly
          className="w-full px-3 py-2 bg-white/10 border border-white/10 rounded-md text-gray-400 cursor-not-allowed"
        />
      </div>
      
      <div className="mb-6">
        <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
          Description
        </label>
        <ReactQuill
          theme="snow"
          value={description}
          onChange={(value) => handleFieldChange('description', value)}
          modules={modules}
          formats={formats}
          className="bg-gray-800 text-white rounded-lg overflow-hidden"
        />
        {errors.description && touched.description && (
          <p className="mt-2 text-sm text-red-400">{errors.description}</p>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-200 mb-1">
            Date de début <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Calendar className="h-5 w-5 text-yellow-400/70" aria-hidden="true" />
            </div>
            <input
              type="datetime-local"
              id="startDate"
              name="startDate"
              value={startDate}
              onChange={(e) => handleFieldChange('startDate', e.target.value)}
              onBlur={() => setTouched(prev => ({ ...prev, startDate: true }))}
              aria-invalid={errors.startDate ? 'true' : 'false'}
              aria-describedby={errors.startDate ? 'startDate-error' : undefined}
              required
              className={`w-full pl-10 px-3 py-2 bg-white/5 border ${errors.startDate && touched.startDate ? 'border-red-500' : 'border-white/10'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100`}
            />
          </div>
          {errors.startDate && touched.startDate && (
            <p id="startDate-error" className="mt-1 text-sm text-red-400">{errors.startDate}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-200 mb-1">
            Date de fin
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Calendar className="h-5 w-5 text-yellow-400/70" aria-hidden="true" />
            </div>
            <input
              type="datetime-local"
              id="endDate"
              name="endDate"
              value={endDate}
              onChange={(e) => handleFieldChange('endDate', e.target.value)}
              onBlur={() => setTouched(prev => ({ ...prev, endDate: true }))}
              aria-invalid={errors.endDate ? 'true' : 'false'}
              aria-describedby={errors.endDate ? 'endDate-error' : undefined}
              className={`w-full pl-10 px-3 py-2 bg-white/5 border ${errors.endDate && touched.endDate ? 'border-red-500' : 'border-white/10'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100`}
            />
          </div>
          {errors.endDate && touched.endDate && (
            <p id="endDate-error" className="mt-1 text-sm text-red-400">{errors.endDate}</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="location" className="block text-sm font-medium text-gray-200 mb-1">
            Lieu
          </label>
          <input
            type="text"
            id="location"
            name="location"
            value={location}
            onChange={(e) => handleFieldChange('location', e.target.value)}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100 placeholder-gray-400"
          />
        </div>
        
        <div>
          <label htmlFor="capacity" className="block text-sm font-medium text-gray-200 mb-1">
            Capacité
          </label>
          <input
            type="number"
            id="capacity"
            name="capacity"
            value={capacity}
            onChange={(e) => handleFieldChange('capacity', e.target.value)}
            onBlur={() => setTouched(prev => ({ ...prev, capacity: true }))}
            aria-invalid={errors.capacity ? 'true' : 'false'}
            aria-describedby={errors.capacity ? 'capacity-error' : undefined}
            min="1"
            className={`w-full px-3 py-2 bg-white/5 border ${errors.capacity && touched.capacity ? 'border-red-500' : 'border-white/10'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100`}
          />
          {errors.capacity && touched.capacity && (
            <p id="capacity-error" className="mt-1 text-sm text-red-400">{errors.capacity}</p>
          )}
        </div>
      </div>
      
      <div>
        <label htmlFor="isVirtual" className="flex items-center">
          <input
            type="checkbox"
            id="isVirtual"
            name="isVirtual"
            checked={isVirtual}
            onChange={(e) => handleFieldChange('isVirtual', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <span className="ml-2 text-sm text-gray-200">Événement virtuel</span>
        </label>
        <p className="text-xs text-gray-400 mt-1">
          Cochez cette case si l'événement se déroule en ligne.
        </p>
      </div>
      
      <div>
        <label htmlFor="contestId" className="block text-sm font-medium text-gray-200 mb-1">
          Concours associé
        </label>
        <select
          id="contestId"
          name="contestId"
          value={contestId}
          onChange={(e) => handleFieldChange('contestId', e.target.value)}
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100"
        >
          <option value="" className="bg-gray-800">Aucun</option>
          {loadingContests ? (
            <option disabled className="bg-gray-800">Chargement des concours...</option>
          ) : contests && contests.length > 0 ? (
            contests.map(contest => (
              <option key={contest.id} value={contest.id} className="bg-gray-800">
                {contest.name}
              </option>
            ))
          ) : (
            <option disabled className="bg-gray-800">Aucun concours disponible</option>
          )}
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-200 mb-1">
          Image de l'événement
        </label>
        
        <div className="mt-1">
          <div 
            className={`relative w-full rounded-xl border-2 border-dashed ${
              isDragging ? 'border-yellow-400 bg-white/10' : 'border-white/20 bg-white/5'
            } transition-colors duration-200`}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isDragging) setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
            }}
            onDrop={handleImageDrop}
          >
            <div className="p-6 text-center">
              {imageUrl ? (
                <div className="relative mx-auto w-64 h-48">
                  <img
                    src={imageUrl}
                    alt="Aperçu de l'image"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setImageUrl('')}
                    className="absolute -top-2 -right-2 bg-red-500/90 text-white rounded-full p-1.5 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-colors"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  {isUploading ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="h-10 w-10 animate-spin text-yellow-400 mb-2" />
                      <p className="text-sm text-gray-300">Téléchargement en cours...</p>
                    </div>
                  ) : (
                    <>
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/5 mb-3">
                        <ImageIcon className="h-8 w-8 text-yellow-400/70" />
                      </div>
                      <div className="text-sm text-gray-300">
                        <p className="font-medium">Glissez et déposez votre image ici</p>
                        <p className="text-xs text-gray-400 mt-1">ou</p>
                      </div>
                      <label
                        htmlFor="image-upload"
                        className="mt-2 inline-flex items-center px-4 py-2 rounded-lg font-medium text-sm text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors cursor-pointer"
                      >
                        Sélectionner un fichier
                      </label>
                      <p className="mt-2 text-xs text-gray-400">
                        PNG, JPG, GIF jusqu'à 5MB
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
            <input
              type="file"
              id="image-upload"
              accept={ACCEPTED_IMAGE_TYPES.join(',')}
              onChange={handleImageInputChange}
              className="hidden"
              disabled={isUploading}
            />
          </div>
        </div>
      </div>
      
      <div className="flex justify-end pt-6 border-t border-white/10">
        <button
          type="submit"
          disabled={isSubmitting || isUploading}
          className={`inline-flex items-center px-6 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-yellow-400 hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 ${
            (isSubmitting || isUploading) ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-900 mr-2"></div>
              Enregistrement...
            </>
          ) : (
            'Enregistrer l\'événement'
          )}
        </button>
      </div>
    </form>
  );
};

export default EventForm;
